import { useEffect, useRef, useState, type PointerEvent } from 'react';
import { chartCeiling, dateLabel, formatValue, lineSegments, type Definition, type TimelinePoint } from './kpiTimeline';

export function KpiTimelineChart({ points, definition, benchmark }: {
  points: TimelinePoint[]; definition: Definition; benchmark: number;
}) {
  const host = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(620);
  const [active, setActive] = useState<number | null>(null);
  const [focusTrend, setFocusTrend] = useState(false);
  useEffect(() => {
    const observer = new ResizeObserver(([entry]) => setWidth(Math.max(260, entry.contentRect.width)));
    if (host.current) observer.observe(host.current);
    return () => observer.disconnect();
  }, []);
  useEffect(() => setActive(null), [points]);
  const height = 246;
  const bounds = { left: definition.format === 'currency' ? 78 : 54, right: 12, top: 24, bottom: 37 };
  const plotWidth = width - bounds.left - bounds.right;
  const values = points.map(point => point.values[definition.key] ?? null);
  const ceiling = chartCeiling(values, focusTrend ? 0 : benchmark, definition.format);
  const x = (index: number) => bounds.left + (points.length === 1 ? plotWidth / 2 : index / Math.max(1, points.length - 1) * plotWidth);
  const y = (value: number) => bounds.top + (1 - value / ceiling) * (height - bounds.top - bounds.bottom);
  const tickIndexes = [...new Set([0, Math.floor((points.length - 1) / 2), points.length - 1])].filter(index => index >= 0 && index < points.length);
  let lastValid = -1;
  for (let index = values.length - 1; index >= 0; index--) {
    if (values[index] !== null && Number.isFinite(values[index])) { lastValid = index; break; }
  }
  const select = (event: PointerEvent<SVGSVGElement>) => {
    if (!points.length) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const localX = (event.clientX - rect.left) / rect.width * width;
    setActive(Math.max(0, Math.min(points.length - 1, Math.round((localX - bounds.left) / plotWidth * (points.length - 1)))));
  };
  const selected = active === null ? null : points[active];
  return <div className="kpi-chart" ref={host}>
    <div className="kpi-chart__scale" aria-label="สเกลกราฟ">
      <button type="button" aria-pressed={!focusTrend} onClick={() => setFocusTrend(false)}>เทียบเป้า</button>
      <button type="button" aria-pressed={focusTrend} onClick={() => setFocusTrend(true)}>ดูการเปลี่ยนแปลง</button>
    </div>
    {benchmark > ceiling && <div className="kpi-chart__outside">เป้า {formatValue(benchmark, definition.format)} อยู่นอกสเกลนี้</div>}
    <svg viewBox={`0 0 ${width} ${height}`} role="img" tabIndex={0}
      aria-label={`${definition.label} รายวัน พร้อมเส้นเป้า Day 60 ใช้ลูกศรซ้ายขวาเพื่อดูแต่ละวัน`}
      onPointerMove={select} onPointerDown={select} onPointerLeave={() => setActive(null)}
      onKeyDown={event => {
        if (!points.length || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        setActive(index => event.key === 'Home' ? 0 : event.key === 'End' ? points.length - 1
          : Math.max(0, Math.min(points.length - 1, (index ?? points.length - 1) + (event.key === 'ArrowLeft' ? -1 : 1))));
      }}>
      {[0, .25, .5, .75, 1].map(ratio => <g key={ratio}>
        <line className="kpi-chart__grid" x1={bounds.left} x2={width - bounds.right} y1={y(ceiling * ratio)} y2={y(ceiling * ratio)} />
        <text className="kpi-chart__axis" x={bounds.left - 8} y={y(ceiling * ratio) + 4} textAnchor="end">{formatValue(ceiling * ratio, definition.format, true)}</text>
      </g>)}
      {benchmark <= ceiling && <><line className="kpi-chart__benchmark" x1={bounds.left} x2={width - bounds.right} y1={y(benchmark)} y2={y(benchmark)} />
        <text className="kpi-chart__goal" x={width - bounds.right} y={y(benchmark) - 7} textAnchor="end">เป้า {formatValue(benchmark, definition.format)}</text></>}
      {lineSegments(values).map((segment, index) => segment.length === 1
        ? <circle className="kpi-chart__dot" key={index} cx={x(segment[0].index)} cy={y(segment[0].value)} r="4" />
        : <polyline className="kpi-chart__line" key={index} points={segment.map(point => `${x(point.index)},${y(point.value)}`).join(' ')} />)}
      {lastValid >= 0 && <circle className="kpi-chart__dot" cx={x(lastValid)} cy={y(values[lastValid]!)} r="4" />}
      {tickIndexes.map(index => <text className="kpi-chart__axis" key={index} x={x(index)} y={height - 10}
        textAnchor={index === 0 && points.length > 1 ? 'start' : index === points.length - 1 && points.length > 1 ? 'end' : 'middle'}>{dateLabel(points[index].date)}</text>)}
      {active !== null && <line className="kpi-chart__cursor" x1={x(active)} x2={x(active)} y1={bounds.top} y2={height - bounds.bottom} />}
      {active !== null && values[active] !== null && <circle className="kpi-chart__dot" cx={x(active)} cy={y(values[active]!)} r="5" />}
    </svg>
    {selected && <div className="kpi-chart__tooltip" role="status" style={{ left: Math.max(4, Math.min(width - 200, x(active!) - 90)) }}>
      <b>{dateLabel(selected.date, true)}</b><span>{formatValue(selected.values[definition.key], definition.format)}</span>
      <small>Benchmark: {formatValue(benchmark, definition.format)}</small>
    </div>}
    {lastValid < 0 && <div className="kpi-chart__empty">ยังไม่มีข้อมูลพอคำนวณ KPI ในช่วงนี้</div>}
  </div>;
}
